import CurrentLocationIcon from './icons/CurrentLocationIcon';
import { Box, Button, ButtonProps, styled } from '@mui/material';
import { CogIcon } from './page-overlay-loader/LoaderWithPageOverlayStyle';
import { currentLocationStatus } from '../constants/error';
import { getUserLocationFromGeocode } from '../utils/googlePlaces';
import { palette } from '../theme/palette';
import { setUserLocationEpicAction } from '../redux/slice/locationSlice';
import { useAppDispatch } from '../redux/store';
import { useEffect, useState } from 'react';
import {
  CurrentLocationStatus,
  GetUserLocationFromGeocodeResponseType,
  UserLocation,
} from '../types/location';

interface CurrentLocationProps extends ButtonProps {
  preOnClickHandler?: () => void;
}

const IconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  color: ${palette.primary.main};
`;

function CurrentLocation({ preOnClickHandler, ...rest }: CurrentLocationProps) {
  const dispatch = useAppDispatch();

  const [currentLocation, setCurrentLocation] = useState<CurrentLocationStatus>(
    CurrentLocationStatus.NONE,
  );

  useEffect(() => {
    const subscription = currentLocationStatus.subscribe((e) => {
      setCurrentLocation(e);
    });
    return () => subscription.unsubscribe();
  }, []);

  const getCurrentPosition = () => {
    if (preOnClickHandler) {
      preOnClickHandler();
    }

    currentLocationStatus.next(CurrentLocationStatus.IS_LOADING);

    navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
      if (permissionStatus.state === 'granted' || permissionStatus.state === 'prompt') {
        navigator.geolocation.getCurrentPosition(
          async (position: GeolocationPosition) => {
            // Permission has been granted, proceed with obtaining the current position
            try {
              const getUserLocationFromGeocodeResponse: GetUserLocationFromGeocodeResponseType | null =
                await getUserLocationFromGeocode(
                  position.coords.latitude,
                  position.coords.longitude,
                );

              if (!getUserLocationFromGeocodeResponse) {
                currentLocationStatus.next(CurrentLocationStatus.ERROR);
                return;
              }

              const { postalCodeComponent, countryComponent, cityComponent, stateComponent } =
                getUserLocationFromGeocodeResponse;

              if (countryComponent?.short_name !== 'US') {
                currentLocationStatus.next(CurrentLocationStatus.BAD_CURRENT_LOCATION);
                return;
              } else if (!postalCodeComponent || !cityComponent || !stateComponent) {
                currentLocationStatus.next(CurrentLocationStatus.ERROR);
                return;
              }

              const userLocation: UserLocation = {
                address: postalCodeComponent.formatted_address,
                place_id: postalCodeComponent.place_id,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                city: cityComponent?.long_name,
                state: stateComponent?.long_name,
              };

              dispatch(
                setUserLocationEpicAction({
                  userLocation: userLocation,
                }),
              );
              currentLocationStatus.next(CurrentLocationStatus.NONE);
            } catch (error) {
              currentLocationStatus.next(CurrentLocationStatus.ERROR);
            }
          },
          () => {
            // Permission has been denied or the user has chosen to block location access
            currentLocationStatus.next(CurrentLocationStatus.BROWSER_PERMISSION_NOT_GRANTED);
          },
        );
      } else if (permissionStatus.state === 'denied') {
        // Permission has been denied or the user has chosen to block location access
        currentLocationStatus.next(CurrentLocationStatus.BROWSER_PERMISSION_NOT_GRANTED);
      }
    });
  };

  return (
    <Button
      disabled={currentLocation === CurrentLocationStatus.IS_LOADING}
      startIcon={
        <IconContainer>
          {currentLocation === CurrentLocationStatus.IS_LOADING ? (
            <CogIcon />
          ) : (
            <CurrentLocationIcon />
          )}
        </IconContainer>
      }
      onClick={getCurrentPosition}
      {...rest}
    >
      Use my current location
    </Button>
  );
}

export default CurrentLocation;
